import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import { FulfillmentOrderLineItem } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import PackageSearchIcon from "@redotech/redo-icons/package-search.svg";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { ProductListItem } from "@redotech/redo-web/product-list-item";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";

interface PackingSlipBinModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  productsWithoutBins: FulfillmentOrderLineItem[];
  onProceedWithoutBin: () => void;
  onSaveAndContinue: () => void;
  onExit: () => void;
}

export const PackingSlipBinModal = memo(function PackingSlipBinModal({
  open,
  setOpen,
  productsWithoutBins,
  onProceedWithoutBin,
  onSaveAndContinue,
  onExit,
}: PackingSlipBinModalProps) {
  const outboundLabelsRpcClient = useRequiredContext(
    RedoOutboundLabelsRpcClientContext,
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [binValue, setBinValue] = useState("");
  const [skipBinAssignment, setSkipBinAssignment] = useState(false);

  // Reset state when modal opens/closes or product changes
  useEffect(() => {
    if (open) {
      setCurrentIndex(0);
      setBinValue("");
      setSkipBinAssignment(false);
    }
  }, [open]);

  // Clear input when moving to next product
  useEffect(() => {
    setBinValue("");
    setSkipBinAssignment(false);
  }, [currentIndex]);

  const currentProduct = productsWithoutBins[currentIndex];
  const isLastProduct = currentIndex === productsWithoutBins.length - 1;

  const [saveLoad, triggerSave] = useTriggerLoad(async () => {
    if (!currentProduct?.variantId) {
      toast("Cannot save bin - product missing variant ID", {
        variant: "error",
      });
      return;
    }

    if (!skipBinAssignment && (!binValue || !binValue.trim())) {
      toast(
        "Please enter a bin location or check 'This item does not require a bin assignment'",
        { variant: "error" },
      );
      return;
    }

    try {
      const binValueToSave = skipBinAssignment ? "-" : binValue.trim();
      const result = await outboundLabelsRpcClient.updateVariantBinMetafield({
        variantId: currentProduct.variantId,
        binValue: binValueToSave,
      });

      if (!result.success) {
        toast("Failed to save bin location", { variant: "error" });
        return;
      }
    } catch (error) {
      console.error("Error saving bin location:", error);
      toast("Error saving bin location", { variant: "error" });
      return;
    }

    // Move to next product or finish
    if (isLastProduct) {
      onSaveAndContinue();
    } else {
      setCurrentIndex(currentIndex + 1);
    }
  });

  const handleExit = useHandler(() => {
    setOpen(false);
    onExit();
  });

  const handleProceedWithoutBin = useHandler(() => {
    // Skip current product and move to next, or exit if this was the last product
    if (isLastProduct) {
      setOpen(false);
      onSaveAndContinue();
    } else {
      setCurrentIndex(currentIndex + 1);
    }
  });

  if (!currentProduct) {
    return null;
  }

  return (
    <RedoModal
      hideFooter
      isOpen={open}
      modalSize="sm"
      onModalCloseRequested={handleExit}
      subtitle="The following product is missing its bin location. Enter the location or skip this product to continue."
      theme="brand"
      title={`Bin number missing (${currentIndex + 1} of ${productsWithoutBins.length})`}
      TitleIcon={PackageSearchIcon}
    >
      <Flex dir="column" gap="3xl">
        <ProductListItem
          disableTooltip={false}
          imageUrl={currentProduct.imageUrl}
          sku={currentProduct.sku}
          title={currentProduct.title || "Untitled"}
          variantTitle={currentProduct.variantTitle}
        />

        <Flex dir="column" gap="3xl">
          <RedoTextInput
            label="Bin location"
            placeholder="Enter the bin location for this product..."
            setValue={setBinValue}
            size="sm"
            state={skipBinAssignment ? "disabled" : undefined}
            value={binValue}
          />

          <Divider />
          <Flex flexDirection="column" gap="xs" pl="lg">
            <RedoCheckbox
              label="This item does not require a bin assignment"
              setValue={(value) => setSkipBinAssignment(value === true)}
              size="sm"
              value={skipBinAssignment}
            />
            <Text fontSize="xxs" textColor="tertiary">
              This option will set the bin in Shopify to indicate no bin is
              required
            </Text>
          </Flex>
        </Flex>

        <Flex
          align="center"
          dir="row"
          gap="md"
          justify="space-between"
          pb="3xl"
          pt="lg"
          w="full"
        >
          <RedoButton
            hierarchy="tertiary"
            onClick={handleProceedWithoutBin}
            size="md"
            text={isLastProduct ? "Skip and continue" : "Skip this product"}
            theme="normal"
          />
          <Flex>
            <RedoButton
              hierarchy="secondary"
              onClick={handleExit}
              size="md"
              text="Exit"
              theme="normal"
            />
            <RedoButton
              disabled={!skipBinAssignment && (!binValue || !binValue.trim())}
              hierarchy="primary"
              onClick={() => triggerSave()}
              pending={saveLoad.pending}
              size="md"
              text={isLastProduct ? "Save and continue" : "Save and next"}
              theme="normal"
            />
          </Flex>
        </Flex>
      </Flex>
    </RedoModal>
  );
});
